// Utility functions for text processing and formatting

/**
 * Safely decode URL-encoded text
 * @param {string} text - The potentially URL-encoded text
 * @returns {string} - The decoded text
 */
export const safeDecodeURIComponent = (text) => {
  if (!text || typeof text !== 'string') {
    return text || '';
  }
  
  try {
    // Check if the text is likely URL-encoded (contains % followed by hex digits)
    if (/%[0-9A-Fa-f]{2}/.test(text)) {
      return decodeURIComponent(text);
    }
    return text;
  } catch (error) {
    console.warn('Failed to decode URI component:', text, error);
    return text; // Return original text if decoding fails
  }
};

/**
 * Get initials from a name (with URL decoding)
 * @param {string} name - The name (potentially URL-encoded)
 * @returns {string} - The initials (max 2 characters)
 */
export const getInitials = (name) => {
  const decodedName = safeDecodeURIComponent(name);
  if (!decodedName) return '??';
  
  return decodedName
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Format and decode display name
 * @param {string} name - The name (potentially URL-encoded)
 * @returns {string} - The decoded and formatted name
 */
export const formatDisplayName = (name) => {
  return safeDecodeURIComponent(name) || 'Unknown User';
};

/**
 * Format date/time from UTC to Asia/Bangkok timezone
 * @param {string|Date} dateInput - Date string (UTC) or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string in Asia/Bangkok timezone
 */
export const formatDateTimeTH = (dateInput, options = {}) => {
  if (!dateInput) return '—';
  
  try {
    // Ensure we have a valid Date object
    let date;
    if (typeof dateInput === 'string') {
      // If string doesn't end with 'Z', assume it's UTC and add 'Z'
      const utcString = dateInput.endsWith('Z') ? dateInput : `${dateInput}Z`;
      date = new Date(utcString);
    } else {
      date = new Date(dateInput);
    }
    
    if (isNaN(date.getTime())) return '—';
    
    const defaultOptions = {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };
    
    return date.toLocaleString('th-TH', defaultOptions);
  } catch (error) {
    console.warn('Failed to format date:', dateInput, error);
    return '—';
  }
};

/**
 * Format date for chart display (short format) - UTC to Bangkok
 * @param {string|Date} dateInput - Date string (UTC) or Date object
 * @returns {string} - Short formatted date in Bangkok timezone
 */
export const formatChartDate = (dateInput) => {
  return formatDateTimeTH(dateInput, {
    month: 'short',
    day: 'numeric',
    hour: undefined,
    minute: undefined
  });
};

/**
 * Format full date/time for tooltips - UTC to Bangkok
 * @param {string|Date} dateInput - Date string (UTC) or Date object
 * @returns {string} - Full formatted date/time in Bangkok timezone
 */
export const formatTooltipDateTime = (dateInput) => {
  return formatDateTimeTH(dateInput, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Debug function to show timezone conversion
 * @param {string|Date} dateInput - Date string (UTC) or Date object
 * @returns {object} - Debug information about timezone conversion
 */
export const debugTimezone = (dateInput) => {
  if (!dateInput) return { error: 'No date input' };
  
  try {
    let date;
    if (typeof dateInput === 'string') {
      const utcString = dateInput.endsWith('Z') ? dateInput : `${dateInput}Z`;
      date = new Date(utcString);
    } else {
      date = new Date(dateInput);
    }
    
    return {
      original: dateInput,
      utcString: date.toISOString(),
      localString: date.toLocaleString(),
      bangkokString: date.toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' }),
      bangkokTime: formatDateTimeTH(dateInput),
      timestamp: date.getTime()
    };
  } catch (error) {
    return { error: error.message, original: dateInput };
  }
};